import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";

import {
  FONT,
  WEIGHT,
  INK,
  INK_SECONDARY,
  INK_MUTED,
  HAIRLINE,
  SURFACE,
  SURFACE_MUTED,
  RADIUS,
} from "../../../../theme/tokens";

import BurstStorageTable from "./BurstStorageTable";
import DatasetsTable from "./DatasetsTable";

// A framed section with a quiet, uppercase header band — used for each
// block of the profile so the drawer reads as a structured document.
const ProfileSection = ({ title, children }) => (
  <Box
    sx={{
      border: `1px solid ${HAIRLINE}`,
      borderRadius: `${RADIUS.md}px`,
      bgcolor: SURFACE,
      overflow: "hidden",
      mb: 2.5,
    }}
  >
    <Box
      sx={{
        px: 2.5,
        py: 1.25,
        borderBottom: `1px solid ${HAIRLINE}`,
        bgcolor: SURFACE_MUTED,
      }}
    >
      <Typography
        sx={{
          fontSize: FONT.xs,
          fontWeight: WEIGHT.bold,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: INK_SECONDARY,
        }}
      >
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);
ProfileSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

// Compact label/value summary pill shown under the title.
const Stat = ({ label, value }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "baseline",
      gap: 0.75,
      px: 1.25,
      py: 0.5,
      borderRadius: "999px",
      bgcolor: SURFACE_MUTED,
      border: `1px solid ${HAIRLINE}`,
    }}
  >
    <Typography
      component="span"
      sx={{
        fontSize: FONT.xs,
        color: INK_MUTED,
        fontWeight: WEIGHT.semibold,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </Typography>
    <Typography
      component="span"
      sx={{ fontSize: FONT.sm, color: INK, fontWeight: WEIGHT.semibold }}
    >
      {value}
    </Typography>
  </Box>
);
Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const ExperimentProfile = ({ experiment }) => {
  if (!experiment || !experiment.status || !experiment.status.statefulset) {
    return (
      <Typography sx={{ p: 2, color: INK_SECONDARY }}>
        No experiment profile available.
      </Typography>
    );
  }
  const { statefulset, pods } = experiment.status;
  const userLabel = statefulset.user_label || "N/A";

  const replicaCount = pods?.length || statefulset.spec_replicas || 0;
  const gpuUsage =
    statefulset.gpu_product && statefulset.gpu_count > 0
      ? { [statefulset.gpu_product]: statefulset.gpu_count * replicaCount }
      : {};

  const mountedDatasets = statefulset.mounted_datasets || [];
  const totalGpus = Object.values(gpuUsage).reduce((a, b) => a + b, 0);

  return (
    <Box>
      {/* Summary header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{ fontSize: FONT.xxl, fontWeight: WEIGHT.semibold, color: INK }}
        >
          {userLabel}
        </Typography>
        <Typography
          sx={{
            fontSize: FONT.xs,
            fontFamily: "monospace",
            color: INK_MUTED,
            mt: 0.25,
          }}
        >
          {experiment.workspace_id || "N/A"}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.75 }}>
          <Stat label="Replicas" value={replicaCount} />
          <Stat label="GPUs" value={totalGpus} />
          <Stat label="Datasets" value={mountedDatasets.length} />
        </Box>
      </Box>

      <ProfileSection title="GPU Usage">
        {Object.keys(gpuUsage).length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {Object.entries(gpuUsage).map(([product, count]) => (
              <Box
                key={product}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: FONT.md,
                    color: INK,
                    fontWeight: WEIGHT.medium,
                  }}
                >
                  {product}
                </Typography>
                <Typography
                  sx={{
                    fontSize: FONT.sm,
                    color: INK_SECONDARY,
                    fontWeight: WEIGHT.semibold,
                  }}
                >
                  ×{count}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: FONT.sm, color: INK_MUTED }}>
            No GPUs assigned.
          </Typography>
        )}
      </ProfileSection>

      <ProfileSection title="Burst Storage Usage">
        <BurstStorageTable pods={pods || []} />
      </ProfileSection>

      <ProfileSection title="Datasets">
        <DatasetsTable datasets={mountedDatasets} />
      </ProfileSection>
    </Box>
  );
};
ExperimentProfile.propTypes = {
  experiment: PropTypes.object,
};

export default ExperimentProfile;
